import React, { useState, useEffect } from 'react';
import { Switch } from 'antd';
import { SettingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { DashboardVisibilityConfig } from './dashboard.types';
import styled from 'styled-components';
import PopupComponent from '../../components/PopupComponent';

const SectionContainer = styled.div`
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f8fafc;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: #374151;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const SectionContent = styled.div`
  padding: 16px;
`;

const ToggleItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ComponentName = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`;

const ComponentCount = styled.span`
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 12px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const InfoText = styled.div`
  font-size: 12px;
  color: #6b7280;
  background: #f8fafc;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const PrimaryButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
  }
`;

interface DashboardVisibilitySettingsProps {
  open: boolean;
  onClose: () => void;
  config: DashboardVisibilityConfig;
  onConfigChange: (config: DashboardVisibilityConfig) => Promise<void>;
}

const DashboardVisibilitySettings: React.FC<DashboardVisibilitySettingsProps> = ({
  open,
  onClose,
  config,
  onConfigChange,
}) => {
  const [localConfig, setLocalConfig] = useState<DashboardVisibilityConfig>(config);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local config when config prop changes
  useEffect(() => {
    setLocalConfig(config);
    setHasChanges(false);
  }, [config]);

  const handleToggleChange = (category: keyof DashboardVisibilityConfig, key: string, checked: boolean) => {
    const newConfig = {
      ...localConfig,
      [category]: {
        ...localConfig[category],
        [key]: checked,
      },
    };
    setLocalConfig(newConfig);
    setHasChanges(true);
  };

  const handleSelectAll = (category: keyof DashboardVisibilityConfig, enable: boolean) => {
    const categoryObj = localConfig[category] as Record<string, boolean>;
    const updatedCategory = Object.keys(categoryObj).reduce((acc, key) => {
      acc[key] = enable;
      return acc;
    }, {} as Record<string, boolean>);

    const newConfig = {
      ...localConfig,
      [category]: updatedCategory,
    };
    setLocalConfig(newConfig);
    setHasChanges(true);
  };

  const handleApplyChanges = async () => {
    try {
      setIsSaving(true);
      await onConfigChange(localConfig);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving visibility config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalConfig(config);
    setHasChanges(false);
    onClose();
  };

  const renderToggleSection = (
    title: string,
    category: keyof DashboardVisibilityConfig,
    items: Record<string, string>,
    icon: string
  ) => {
    const categoryConfig = localConfig[category] as Record<string, boolean>;
    const enabledCount = Object.values(categoryConfig).filter(Boolean).length;
    const totalCount = Object.keys(categoryConfig).length;

    return (
      <SectionContainer key={category}>
        <SectionHeader>
          <SectionTitle>
            <span>{icon}</span>
            {title}
            <ComponentCount>{enabledCount}/{totalCount}</ComponentCount>
          </SectionTitle>
          <SectionActions>
            <ActionButton onClick={() => handleSelectAll(category, true)}>
              All
            </ActionButton>
            <ActionButton onClick={() => handleSelectAll(category, false)}>
              None
            </ActionButton>
          </SectionActions>
        </SectionHeader>
        <SectionContent>
          {Object.entries(items).map(([key, label]) => (
            <ToggleItem key={key}>
              <ComponentName>{label}</ComponentName>
              <Switch
                checked={categoryConfig[key]}
                onChange={(checked) => handleToggleChange(category, key, checked)}
                size="small"
              />
            </ToggleItem>
          ))}
        </SectionContent>
      </SectionContainer>
    );
  };

  const chartItems = {
    areaChart: 'Area Chart (Main Graph)',
    leadsBySource: 'Leads by Source',
    leadsByLabel: 'Leads by Labels',
    salesFunnel: 'Sales Funnel',
    taskStatus: 'Task Status Chart',
    callLeaderboard: 'Call Leaderboard',
    userPerformance: 'User Performance Report',
    leadAssignment: 'Lead Assignment Table',
    salesTarget: 'Sales Target Tracking',
    customFields: 'Custom Field Analysis',
    callLogs: 'Call Logs Table',
    userSync: 'User Sync Status',
  };

  const metricItems = {
    secondaryMetrics: 'Secondary Metrics Row',
    tabSpecificMetrics: 'Tab-Specific Metrics',
  };

  const activityItems = {
    activityList: 'Activity List',
  };

  const reportItems = {
    callLeaderboard: 'Call Leaderboard Report',
    callLogs: 'Call Logs Report',
    userPerformance: 'User Performance Report',
    leadAssignment: 'Lead Assignment Report',
    salesFunnel: 'Sales Funnel Analysis',
    activitySummary: 'Activity Summary',
    leadSource: 'Lead Source Analysis',
    leadByLabels: 'Leads by Labels',
    salesTarget: 'Sales Target Tracking',
    taskStatus: 'Task Status Report',
    customFields: 'Custom Field Analysis',
    quotationStatus: 'Quotation Status Report',
    userSyncStatus: 'User Sync Status',
    trends: 'Trends & Analytics',
  };

  const footerContent = (
    <FooterActions>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>
        {hasChanges && '• Unsaved changes'}
        {isSaving && '• Saving...'}
      </div>
      <ActionButtonGroup>
        <SecondaryButton onClick={handleCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton 
          onClick={handleApplyChanges}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Saving...' : 'Apply Changes'}
        </PrimaryButton>
      </ActionButtonGroup>
    </FooterActions>
  );

  return (
    <PopupComponent
      isOpen={open}
      onClose={handleCancel}
      title="Dashboard Visibility Settings"
      footer={footerContent}
      size="md"
      loading={isSaving}
    >
      <InfoText>
        <SettingOutlined />
        Configure which dashboard components to display. Reports marked as visible will auto-open when you load the dashboard. Changes are saved to your profile.
      </InfoText>

      {renderToggleSection('Charts & Visualizations', 'charts', chartItems, '📊')}
      {renderToggleSection('Metrics', 'metrics', metricItems, '📈')}
      {renderToggleSection('Activities', 'activities', activityItems, '📋')}
      {renderToggleSection('Auto-Open Reports (Lazy Loaded)', 'reports', reportItems, '📑')}
    </PopupComponent>
  );
};

export default DashboardVisibilitySettings;